import * as React from 'react';
import { Menu, Transition } from '@headlessui/react';
import { cn } from '../../lib/cn';

interface DropdownItem {
  label?: React.ReactNode;
  onSelect?: () => void;
  href?: string;
  icon?: React.ReactNode;
  danger?: boolean;
  disabled?: boolean;
  separator?: boolean;
}

interface DropdownProps {
  trigger: React.ReactNode;
  items: DropdownItem[];
  align?: 'left' | 'right';
  className?: string;
  menuClassName?: string;
}

export function Dropdown({ trigger, items, align = 'right', className, menuClassName }: DropdownProps) {
  return (
    <Menu as="div" className={cn('relative inline-block text-left', className)}>
      <Menu.Button as={React.Fragment}>{trigger}</Menu.Button>
      <Transition
        as={React.Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items
          className={cn(
            'absolute z-40 mt-2 min-w-[12rem] origin-top rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-elevation-3 focus:outline-none',
            align === 'right' ? 'right-0' : 'left-0',
            menuClassName
          )}
        >
          {items.map((item, idx) =>
            item.separator ? (
              <div key={`sep-${idx}`} className="my-1 h-px bg-border" />
            ) : (
              <Menu.Item key={idx} disabled={item.disabled}>
                {({ active }) => {
                  const className = cn(
                    'flex w-full cursor-pointer items-center gap-2 rounded-sm px-2.5 py-1.5 text-sm transition-colors [&_svg]:h-4 [&_svg]:w-4',
                    active && !item.danger && 'bg-accent text-accent-foreground',
                    active && item.danger && 'bg-destructive/10 text-destructive',
                    !active && item.danger && 'text-destructive',
                    item.disabled && 'cursor-not-allowed opacity-50'
                  );
                  if (item.href) {
                    return (
                      <a href={item.href} className={className}>
                        {item.icon}
                        {item.label}
                      </a>
                    );
                  }
                  return (
                    <button type="button" onClick={item.onSelect} className={className} disabled={item.disabled}>
                      {item.icon}
                      {item.label}
                    </button>
                  );
                }}
              </Menu.Item>
            )
          )}
        </Menu.Items>
      </Transition>
    </Menu>
  );
}
